import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { CalendarIcon, UsersIcon, BuildingIcon, ClockIcon, ShieldCheckIcon, TargetIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
const statValues = [{
  icon: <CalendarIcon className="w-5 h-5" data-id="element-21" data-mp="1nosjpbl8h5ul" />,
  value: 20,
  gradient: 'from-primary-500 to-primary-600'
}, {
  icon: <UsersIcon className="w-5 h-5" data-id="element-22" data-mp="1doi5bnngpsdu" />,
  value: 3500,
  gradient: 'from-cyan-500 to-primary-500'
}, {
  icon: <BuildingIcon className="w-5 h-5" data-id="element-23" data-mp="zy69pero5bhy" />,
  value: 500,
  gradient: 'from-teal-500 to-cyan-500'
}, {
  icon: <TargetIcon className="w-5 h-5" data-id="element-24" data-mp="1400455b9tj9o" />,
  value: 98,
  gradient: 'from-emerald-500 to-primary-500'
}, {
  icon: <ClockIcon className="w-5 h-5" data-id="element-25" data-mp="pzpci02500ng" />,
  value: 14,
  gradient: 'from-primary-600 to-teal-500'
}, {
  icon: <ShieldCheckIcon className="w-5 h-5" data-id="element-26" data-mp="v0d3ilqd8hho" />,
  value: 100,
  gradient: 'from-emerald-500 to-teal-500'
}];
function AnimatedCounter({
  target,
  suffix,
  inView
}: {
  target: number;
  suffix: string;
  inView: boolean;
}) {
  const [count, setCount] = useState(0);
  const hasAnimated = useRef(false);
  useEffect(() => {
    if (!inView || hasAnimated.current) return;
    hasAnimated.current = true;
    const duration = 1800;
    const startTime = performance.now();
    function animate(currentTime: number) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(animate);
    }
    requestAnimationFrame(animate);
  }, [inView, target]);
  return <span data-id="element-27" data-mp="15owu1q972rft">
      {count.toLocaleString('uk-UA')}
      {suffix}
    </span>;
}
function StatCard({
  sv,
  stat,
  index,
  isInView
}: {
  sv: (typeof statValues)[number];
  stat: {
    suffix: string;
    label: string;
    description: string;
  };
  index: number;
  isInView: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  return <motion.div initial={{
    opacity: 0,
    y: 24
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5,
    delay: index * 0.1
  }} className="relative p-4 sm:p-6 rounded-2xl bg-gradient-to-b from-white to-navy-50/80 border border-navy-100/80 text-center group overflow-hidden shadow-sm cursor-default" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} data-id="element-28" data-mp="35ptr1tgvv4if">
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${sv.gradient} rounded-t-2xl`} data-id="element-29" data-mp="2vtqm6funcrrs" />
      <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br ${sv.gradient} text-white mb-4 shadow-lg shadow-primary-500/15 transition-transform duration-200 ${hovered ? 'scale-110' : ''}`} data-id="element-30" data-mp="70wc8lc706kh">
        {sv.icon}
      </div>
      <div className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-navy-900 tracking-tight leading-none" data-id="element-31" data-mp="1rjgjtx333vpr">
        <AnimatedCounter target={sv.value} suffix={stat.suffix} inView={isInView} data-id="element-32" data-mp="1mrp1uaxzuyb8" />
      </div>
      <div className="mt-2 text-sm text-navy-500 font-medium leading-snug" data-id="element-33" data-mp="1hrsx29hh32um">
        {stat.label}
      </div>
      <AnimatePresence data-id="element-34" data-mp="1csg211gg6pgy">
        {hovered && <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 4
      }} transition={{
        duration: 0.2
      }} className="absolute inset-0 flex items-center justify-center p-4 rounded-2xl bg-navy-900/95 backdrop-blur-sm" data-id="element-35" data-mp="17uo6lxglgl80">
            <p className="text-xs sm:text-sm text-white/90 leading-relaxed font-medium text-center" data-id="element-36" data-mp="1pxmzn9m8k2fl">
              {stat.description}
            </p>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function AchievementsSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section className="relative py-16 lg:py-20 bg-white overflow-hidden" id="about" data-id="element-37" data-mp="175u53ariq95r">
      <div className="absolute inset-0 pointer-events-none" data-id="element-38" data-mp="1c086puwrxciv">
        <div className="absolute top-0 left-1/4 w-[300px] h-[300px] rounded-full bg-primary-100/20 blur-3xl hidden sm:block" data-id="element-39" data-mp="y8a5e69b0kx9" />
        <div className="absolute bottom-0 right-1/3 w-[200px] h-[200px] rounded-full bg-cyan-100/20 blur-3xl hidden sm:block" data-id="element-40" data-mp="126hlqzzuvk7x" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-41" data-mp="t8fa7qihx96e">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 lg:gap-5" data-id="element-42" data-mp="9larigha6ua4">
          {statValues.map((sv, index) => {
          const stat = t.achievements.stats[index];
          return <StatCard key={index} sv={sv} stat={stat} index={index} isInView={isInView} data-id="element-43" data-mp="jl1k2cma6z7k" data-mpi={index} />;
        })}
        </div>
      </div>
    </section>;
}